import { Instance, SnapshotOut, types } from 'mobx-state-tree'

import { Profanity, ProfanityOptions } from '@2toad/profanity'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { MessageContractModelType } from '../api-store'
import { ChatKind } from '../api-store/ChatKindEnum'
import { withRootStore } from '../extensions'

const logger = extendLogger(LoggerContexts.ChatStore)

export const ChatMessageModel = types.model('ChatMessageModel').props({
  _id: types.maybe(types.string),
  channelName: types.maybe(types.string),
  userName: types.maybe(types.string),
  message: types.maybe(types.string),
  // chatKind: ChatKind.user || ChatKind.dealer || ChatKind.engine || ChatKind.observer || ChatKind.admin,
  chatKind: types.enumeration<ChatKind>('ChatKind', Object.values(ChatKind)),
  userId: types.string,
  color: types.maybe(types.string),
  timestamp: types.maybe(types.number)
})

export const CelebrationModel = types.model('CelebrationModel').props({
  _id: types.maybe(types.string),
  channelName: types.maybe(types.string),
  userId: types.maybe(types.string),
  emoji: types.maybe(types.string)
})

export const ShowCardsModel = types.model('ShowCardsModel').props({
  _id: types.maybe(types.string),
  channelName: types.maybe(types.string),
  userId: types.maybe(types.string),
  leftCardShown: types.maybe(types.boolean),
  rightCardShown: types.maybe(types.boolean)
})

export const options = new ProfanityOptions()

export interface Chat extends Instance<typeof ChatMessageModel> {}
export interface IChat extends SnapshotOut<typeof ChatMessageModel> {}
export interface ICelebration extends SnapshotOut<typeof CelebrationModel> {}
export interface IShowCards extends SnapshotOut<typeof ShowCardsModel> {}
/**
 * Chat Store Model
 */
export const ChatStoreModel = types
  .model('ChatStoreModel')
  // TODO: These props are not being used, should we remove them?
  .props({
    channelChats: types.optional(types.array(ChatMessageModel), []),
    celebrations: types.optional(types.array(CelebrationModel), []),
    showCards: types.optional(types.array(ShowCardsModel), []),
    isChatting: types.optional(types.boolean, false),
    backgroundedChatQueue: types.optional(types.array(ChatMessageModel), []),
    iGameBackgrounded: types.optional(types.boolean, false),
    typingUsers: types.optional(types.array(types.string), []),
    typingUser: types.optional(types.string, ''),
    isTyping: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .volatile((self) => {
    options.wholeWord = true
    options.grawlix = '*****'
    // With time - add words to be whitelisted
    const whitelistWords = ['hoar', 'nob']
    const profanity = new Profanity(options)
    profanity.whitelist.addWords(whitelistWords)
    return {
      profanity
    }
  })
  .actions((self) => ({
    setIsChatting(value: boolean) {
      self.isChatting = value
    },
    // Adds a chat only locally
    checkDuplicateMessage(chatToCheck: IChat) {
      // We trust player chats, we need to make sure the engine doesn't spit out many dealer chats.
      if (chatToCheck.chatKind === ChatKind.dealer && self.channelChats.length > 0) {
        const dealerChats = self.channelChats.filter((chat) => chat.chatKind === ChatKind.dealer)
        if (dealerChats[dealerChats.length - 1] !== chatToCheck) {
          return false
        }
        return true
      }
      const existingChats = self.channelChats.filter(
        (chat) => chat._id === chatToCheck._id && chat.channelName === chatToCheck.channelName
      )
      if (existingChats.length > 0) {
        return true
      }
      return false
    }
  }))
  .actions((self) => ({
    setIsChatting(value: boolean) {
      self.isChatting = value
    },
    setIsTyping(value: boolean) {
      self.isTyping = value
    },
    setTypingUser(value: string) {
      self.typingUser = value
    },
    clearBackgroundChatQueue() {
      self.backgroundedChatQueue.clear()
    },
    backgroundChatEnqueue(channelName: string, userName: string, message: string, chatKind: ChatKind, color: string) {
      const constructedChat = {
        channelName: channelName,
        userName: userName, // If it's a user-generated chat, this name will be above the chat bubble - else - hidden.
        message: message,
        chatKind: chatKind,
        userId: userName,
        color: color
      }
      self.backgroundedChatQueue.push(constructedChat)
    },
    setIsGameBackgrounded(value: boolean) {
      self.iGameBackgrounded = value
    }
  }))
  .actions((self) => ({
    // This hits API chatMessage endpoint.
    async sendChat(message: string, chatKind: ChatKind, color: string, isTyping = false) {
      const channelName = self.rootStore.communication.channel?.channelName
      const userName = self.rootStore.session.user?.name
      const userId = self.rootStore.session.user?._id || ''
      message = self.profanity.censor(message).toString()
      // @TODO We need better checks here.
      if (message !== '' && channelName && userName !== '' && userName) {
        try {
          // vv incase we want to do some client-side chat loading.
          logger.info('sendChat with', channelName, message, color)

          await self.rootStore.api.mutateChatMessage({
            channelName,
            userName,
            message,
            chatKind,
            color,
            isTyping,
            userId
          })
        } catch (error) {
          console.error('sendChat Error', error)
          throw error
        }
      } else {
        logger.error('sendChat Error: undefined/empty strings', message, channelName)
      }
    },
    async sendCelebration(emoji: string) {
      const channelName = self.rootStore.communication.channel?.channelName ?? ''
      const userId = self.rootStore.session.user?._id ?? ''
      const response = await self.rootStore.api.querySendCelebration(
        {
          channelName,
          userId,
          emoji
        },
        { fetchPolicy: 'no-cache' }
      )
      console.debug('sendCelebration response', response)
    },
    async sendShowCards(leftCardShown: boolean, rightCardShown: boolean) {
      const channelName = self.rootStore.communication.channel?.channelName ?? ''

      const userId = self.rootStore.session.user?._id ?? ''

      const response = await self.rootStore.api.querySendShowCards(
        {
          channelName,
          leftCardShown,
          rightCardShown,
          userId
        },
        { fetchPolicy: 'no-cache' }
      )
      console.debug('sendShowCards response', response)
    },
    async handleCelebrations(incomingCelebration: MessageContractModelType) {
      //@ts-ignore payload is defined as frozen
      const { emoji, kind, userId, _id } = incomingCelebration.payload
      const { channelName } = incomingCelebration

      const newCelebration: ICelebration = {
        channelName,
        emoji,
        userId,
        _id
      }

      if (kind && emoji && userId) {
        const celebrationAlreadyExists = self.celebrations.find((celeb) => celeb._id === newCelebration._id)
        if (!celebrationAlreadyExists) {
          self.celebrations.push(newCelebration)
        }
      }
    },
    async handleShowCards(incomingShowCards: MessageContractModelType) {
      //@ts-ignore payload is defined as frozen
      const { leftCardShown, rightCardShown, kind, userId, _id } = incomingShowCards.payload
      const { channelName } = incomingShowCards

      const newShowCards: IShowCards = {
        channelName,
        leftCardShown,
        rightCardShown,
        userId,
        _id
      }

      if (kind && userId) {
        const existingIndex = self.showCards.findIndex((show) => show.userId === newShowCards.userId)
        if (existingIndex === -1) {
          self.showCards.push(newShowCards)
        } else {
          self.showCards[existingIndex] = newShowCards
        }
      }
    },
    // This gets called from session-messages.
    async handleChat(incomingChat: MessageContractModelType) {
      //@ts-ignore payload is defined as frozen
      const { message, kind, userId, userName, uniqueId, color, timestamp } = incomingChat.payload
      //Todo: Define payload type or create separate modal for payload
      const { channelName } = incomingChat
      const newChat: IChat = {
        channelName,
        userName,
        // uniqueId,
        // messageId,
        message,
        chatKind: kind,
        color,
        userId,
        _id: uniqueId,
        timestamp
      }

      if (kind === ChatKind.typing) {
        self.rootStore.communication.setIsTyping(true)
        self.rootStore.communication.setTypingUser(userId)
      } else if (incomingChat && message && kind && userId && userName) {
        const messageAlreadyExists = self.channelChats.find((chat) => chat._id === newChat._id)
        if (!messageAlreadyExists) {
          self.channelChats.push(newChat)
          logger.info('handleChat successful receipt')
        } else {
          logger.warn('handleChat: Prevented duplicate message.')
        }
      }
    },
    // Clears local copy of channelChats.
    clearChannelChats() {
      self.channelChats.splice(0, self.channelChats.length)
      logger.info('clearChannelChats clearing list during cleanup.')
    },
    clearCelebrations() {
      self.celebrations.splice(0, self.celebrations.length)
    },
    clearShowCards() {
      self.showCards.splice(0, self.showCards.length)
    }
  }))

/**
 * The ChatStore instance.
 */
export type ChatStoreType = Instance<typeof ChatStoreModel>

/**
 * The data of a ChatStore.
 */
export type ChatStoreSnapshot = SnapshotOut<typeof ChatStoreModel>
